/* ===========================
IMPORTS
=========================== */
import React from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
// IMPORT - STYLING
import '../../assets/css/Rules.css';
// IMPORT - COMPONENTS
import Preview from './Preview';
import ModalBundle from './ModalBundle';
// IMPORT - ELEMENTS
import Modal from '../Elements/Modal';
import Loader from '../Elements/Loader';
// IMPORT - Helpers
import Helpers from '../../Helpers';

/* ===========================
CLASS
=========================== */
class RulesList extends React.Component {
  /* ===========================
  DEFAULT
  =========================== */
  /**
   * Main constructor of Component
   * @param {object} props
   */
  constructor(props) {
    super(props);

    // state
    this.state = {
      // bundle rules
      errors: null,
      data: [],
      page: 1,
      limit: 250,
      loading: false,
      submitted: false,

      // matchers
      optionsMatchers: [
        'name',
        'device_id',
        'timezone',
        'dpc_sn',
        'product_sn',
        'group_name',
        'tenant_name',
        'hw_info',
        'product_partnum',
        'firmware_version',
        'icanvasplayer_version',
        'adsync_version',
        'superuserservice_version',
        'logomonitor_version',
        'stack_host',
        'tag',
      ],

      // modal
      modals: {
        bundle: false,
      },
      modalData: null,
      modalSubmitted: false,
      loadingModal: false,

      // preview
      preview: ((new Date()).getTime()),

      // dragging
      posYOffset: 0,
      pos: {
        x: 0,
        y: 0,
      },
    };

    // bind function
    this.get = this.get.bind(this);
    this.update = this.update.bind(this);

    // updates
    this.updateShowHide = this.updateShowHide.bind(this);
    this.updateInputState = this.updateInputState.bind(this);
    this.updateCheckboxState = this.updateCheckboxState.bind(this);
    this.updateRulePreview = this.updateRulePreview.bind(this);
    this.updateMatcherSelectionState = this.updateMatcherSelectionState.bind(this);
    this.updateMatcherState = this.updateMatcherState.bind(this);
    this.updateShowHideModal = this.updateShowHideModal.bind(this);
    this.updateLoadingModal = this.updateLoadingModal.bind(this);
    this.updateModalSubmitted = this.updateModalSubmitted.bind(this);
    this.updateOrder = this.updateOrder.bind(this);

    // actions
    this.onClickReset = this.onClickReset.bind(this);
    this.onClickRemoveRule = this.onClickRemoveRule.bind(this);
    this.onClickRemoveMatcher = this.onClickRemoveMatcher.bind(this);
    this.onClickAddRule = this.onClickAddRule.bind(this);
    this.onClickPublishRules = this.onClickPublishRules.bind(this);
  }

  /**
   */
  componentDidMount() {
    // remove loading
    this.props.updateLoadingApp(false);
    this.props.updateLoadingContent(false);

    // make initial request
    this.get();
  }

  /* ===========================
  FUNCTIONS
  =========================== */
  /**
   * @param {object} params
   * @param {function} callback
   * @param {function} failback
   */
  get(params, callback, failback) {
    this.setState({
      errors: null,
      loading: true,
      priority: null,
      preview: (new Date()).getTime(),
    }, () => {
      // Make initial request
      const defaultPayload = {
        page: this.state.page,
        limit: this.state.limit,
      };

      RulesList.requestGet(
        (params || defaultPayload),
        // callback
        (response) => {
          if (callback) {
            callback(response);
          } else {
            this.setState({
              data: response.bundle_rules.map((item) => ({ ...item, ...{ show: false, matcherSelection: '', dragging: false } })),
              submitted: false,
              loading: false,
            });
          }
        },
        // failback
        (error) => {
          if (failback) {
            failback(error);
          } else {
            this.setState({
              errors: error,
              submitted: false,
              loading: false,
            });
          }
        },
      );
    });
  }

  /**
   * @param {object} payload
   * @param {function} callback
   * @param {function} failback
   */
  update(payload, callback, failback) {
    this.setState({
      errors: null,
      submitted: true,
      priority: null,
      preview: (new Date()).getTime(),
    }, () => {
      const { data: payloadCopy } = this.state;
      const defaultPayload = {
        bundle_rules: [...payloadCopy].map((_item) => {
          const item = { ..._item };
          if (item.show && (typeof item.show === 'boolean')) {
            delete item.show;
          }

          if ('updated_at' in item) {
            delete item.updated_at;
          }

          if ('matcherSelection' in item) {
            delete item.matcherSelection;
          }

          if ('dragging' in item) {
            delete item.dragging;
          }

          if ('bundle' in item) {
            item.bundle_id = (item.bundle) ? item.bundle.id : null;
            delete item.bundle;
          }

          if ('show' in item) {
            delete item.show;
          }

          if ('priority' in item) {
            delete item.priority;
          }

          return item;
        }),
      };

      RulesList.requestUpdate(
        (payload || defaultPayload),
        // callback
        (response) => {
          if (callback) {
            callback(response);
          } else {
            this.get();
          }
        },
        // failback
        (error) => {
          if (failback) {
            failback(error);
          } else {
            this.setState({
              errors: error,
              loading: false,
              submitted: false,
            });
          }
        },
      );
    });
  }

  /**
   * @param {object} params
   * @param {function} callback
   * @param {function} failback
   */
  static requestGet(params, callback, failback) {
    let query = '';
    if (params && Object.keys(params).length > 0) {
      Object.keys(params).map((key) => {
        query += `${key}=${params[key]}&`;
        return key;
      });
      query = `?${query}`;
    }

    Helpers.httpRequest(
      `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_API_VERSION}/bundle_rules${query}`,
      'get',
      null,
      // callback
      (response) => {
        if (callback) {
          callback(response);
        }
      },
      // failback
      (error) => {
        if (failback) {
          failback(error);
        }
      },
    );
  }

  /**
   * @param {object} payload
   * @param {function} callback
   * @param {function} failback
   */
  static requestUpdate(payload, callback, failback) {
    Helpers.httpRequest(
      `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_API_VERSION}/bundle_rules`,
      'post',
      JSON.stringify(payload),
      // callback
      (response) => {
        if (callback) {
          callback(response);
        }
      },
      // failback
      (error) => {
        if (failback) {
          failback(error);
        }
      },
    );
  }

  /**
   * @param {integer} index
   */
  updateShowHide(index) {
    const { data } = this.state;
    data[index].show = !data[index].show;

    this.setState({
      data,
    });
  }

  /**
   * @param {Event} event
   * @param {index} index
   */
  updateInputState(event, index) {
    const { data } = this.state;
    data[index][event.target.name] = event.target.value;
    this.setState({
      data,
    });
  }

  /**
   * @param {Event} event
   * @param {integer} index
   */
  updateCheckboxState(event, index) {
    const { data } = this.state;
    if (data[index][event.target.name]) {
      data[index][event.target.name] = false;
    } else {
      data[index][event.target.name] = true;
    }
    this.setState({
      data,
    });
  }

  /**
   * @param {Event} event
   * @param {integer} index
   * @param {matcher} matcher
   */
  updateMatcherSelectionState(event, index) {
    if (event.target.value && event.target.value.length > 0) {
      const { data } = this.state;
      data[index].matcherSelection = event.target.value;
      this.setState({
        data,
      });
    }
  }

  /**
   * @param {Event} event
   * @param {index} index
   */
  updateMatcherState(event, index) {
    const { data } = this.state;
    data[index].matchers[event.target.name] = event.target.value;
    this.setState({
      priority: index,
      data,
      preview: ((new Date()).getTime()),
    });
  }

  /**
   * @param {Event} event
   * @param {integer} index
   */
  updateRulePreview(event, index) {
    this.setState({
      priority: index,
      preview: ((new Date()).getTime()),
    });
  }

  /**
   * @param {boolean} loading
   */
  updateLoadingModal(loading) {
    this.setState({
      loadingModal: loading,
    });
  }

  /**
   * @param {boolean} submitted
   */
  updateModalSubmitted(submitted) {
    this.setState({
      modalSubmitted: submitted,
    });
  }

  /**
   * @param {string} direction
   * @param {integer} key
   */
  updateOrder(direction, key) {
    let replaceWith = null;
    let temp = null;
    const { data } = this.state;

    switch (direction) {
      case 'up':
        replaceWith = ((key - 1) > 0) ? (key - 1) : 0;
        break;
      case 'down':
        replaceWith = ((key + 1) < (data.length - 1)) ? (key + 1) : (data.length - 1);
        break;
      default:
        // none
        break;
    }

    // swap
    temp = data[replaceWith];
    data[replaceWith] = data[key];
    data[key] = temp;

    data.map((item, k) => ({ ...item, priority: k }));

    this.setState({
      data,
      priority: null,
      preview: ((new Date()).getTime()),
    });
  }

  /**
   * @param {string} modal
   * @param {boolean} show
   */
  updateShowHideModal(modal, show) {
    const { modals } = this.state;
    modals[modal] = show;
    this.setState({
      modals,
    });
  }

  /**
   * @param {Event} event
   */
  onClickReset() {
    this.get();
  }

  /**
   * @param {Event} event
   * @param {integer} index
   */
  onClickRemoveRule(_event, index) {
    this.setState((prevState) => ({
      data: [...prevState.data.slice(0, index), ...prevState.data.slice((index + 1))],
      priority: null,
      preview: (((new Date()).getTime()) + 1),
    }));
  }

  /**
   * @param {Event} event
   * @param {integer} index
   * @param {string} matcher
   */
  onClickRemoveMatcher(_event, index, matcher) {
    const { data } = this.state;
    delete data[index].matchers[matcher];
    this.setState({
      data,
      preview: ((new Date()).getTime()),
      priority: index,
    });
  }

  /**
   * @param {Event} event
   * @param {integer} index
   */
  onClickAddMatcher(_event, index) {
    const { data } = this.state;
    const matcher = data[index].matcherSelection;
    if (matcher && matcher.length > 0) {
      if (!data[index].matchers[matcher]) {
        data[index].matchers[matcher] = '';
      }
      data[index].matcherSelection = '';

      this.setState({
        data,
      });
    }
  }

  /**
   * @param {Event} event
   */
  onClickAddRule() {
    this.setState((prevState) => {
      const newRule = [{
        name: '',
        description: '',
        priority: prevState.data.length,
        matchers: {},
      }];

      return {
        data: [...prevState.data, ...newRule],
      };
    });
  }

  /**
   * @param {Event} event
   */
  onClickPublishRules() {
    this.update();
  }

  /* ===========================
  RENDER
  =========================== */
  render() {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col col-6 col-left">
            <div className="page-header">
              <button onClick={this.onClickReset} type="button" disabled={this.state.loading || this.state.submitted} className="btn btn-rounded btn-info float-right">Reset</button>

              <button onClick={this.onClickAddRule} type="button" disabled={this.state.loading || this.state.submitted} className="btn btn-rounded btn-outline btn-primary float-right">Add Rule</button>
              {(this.state.data && this.state.data.length > 0)
                ? (<button onClick={this.onClickPublishRules} type="submit" disabled={this.state.loading || this.state.submitted} className="btn btn-rounded btn-primary float-right">Publish Rules</button>)
                : null}
              <h1>Rules</h1>
            </div>

            {(this.state.errors)
              ? (
                <div className="errors">
                  <div className="alert alert-danger">{(this.state.errors.message) ? this.state.errors.message : 'Something went wrong.'}</div>
                </div>
              )
              : null}

            {(this.state.loading)
              ? (<Loader />)
              : (
                <div className="Rules-Container">
                  {(this.state.data.length > 0) ? (<div className="Rules-Line" />) : null}
                  <div className="Rules">
                    {(this.state.data.length === 0)
                      ? (<p>No Rules Yet</p>)
                      : (this.state.data.map((item, key) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <div key={`Rule-${key}`} className="Rule-Wrap">
                          <div className={`Rule-Placeholder ${(item.dragging) ? 'drag' : ''}`} />

                          <div className={`Rule card ${(item.dragging) ? 'drag' : ''}`} style={{ top: (item.dragging) ? (this.state.pos.y - (this.state.posYOffset * (key + 1)) - 15) : 'auto' }}>
                            <div className="Rule-Header card-header">
                              <div className="btn-group float-right" role="group">
                                <button disabled={this.state.submitted} type="button" aria-label="update-rule" onClick={(e) => this.updateRulePreview(e, key)} className="btn btn-secondary"><i className="fa fa-exchange" /></button>
                                <button disabled={this.state.submitted} type="button" onClick={() => this.updateShowHide(key)} className={`btn ${((item.show) ? 'btn-dark' : 'btn-secondary')}`}>{(item.show) ? (<i className="fa fa-caret-up" />) : (<i className="fa fa-caret-down" />) }</button>
                                <button disabled={this.state.submitted} type="button" aria-label="remove-rule" onClick={(e) => this.onClickRemoveRule(e, key)} className="btn btn-danger"><i className="fa fa-close" /></button>
                              </div>
                              <div className="btn-group float-left" role="group">
                                <button className="btn btn-info" type="button">{(key + 1)}</button>
                                <button className="btn btn-secondary" aria-label="up" type="button" onClick={() => this.updateOrder('up', key)} disabled={this.state.submitted || key === 0}><i className="fa fa-long-arrow-up" /></button>
                                <button className="btn btn-secondary" aria-label="down" type="button" onClick={() => this.updateOrder('down', key)} disabled={this.state.submitted || ((key + 1) === this.state.data.length)}><i className="fa fa-long-arrow-down" /></button>
                              </div>
                              {item.name}
                            </div>

                            {(item.show)
                              ? (
                                <div>
                                  <div className="Rule-Body card-body">
                                    <div className="form-group">
                                      <label htmlFor="name">Name</label>
                                      <input id="name" disabled={this.state.submitted} onChange={(e) => this.updateInputState(e, key)} className="form-control" type="text" name="name" placeholder="Name" value={item.name} />
                                    </div>
                                    <div className="form-group">
                                      <label htmlFor="bundle">Bundle</label>
                                      <br />
                                      <div className="btn-group btn-block">
                                        {(item.bundle && (item.bundle.id || item.bundle.name))
                                          ? (<button id="bundle" disabled={this.state.submitted} type="button" className="btn btn-rounded btn-primary">{item.bundle.name || item.bundle.id}</button>)
                                          : null}
                                        <button disabled={this.state.submitted} onClick={() => { this.setState({ modalData: item }, () => { this.updateShowHideModal('bundle', true); }); }} type="button" className="btn btn-rounded btn-primary">Edit</button>
                                      </div>
                                    </div>
                                    <div className="form-group">
                                      <div className="form-check">
                                        <input id="maintenance_schedule_override" disabled={this.state.submitted} checked={(item.maintenance_schedule_override === true)} name="maintenance_schedule_override" onChange={(e) => this.updateCheckboxState(e, key)} className="form-check-input" type="checkbox" value={item.maintenance_schedule_override} />
                                        <label className="form-check-label" htmlFor="maintenance_schedule_override">Maintenance Schedule Override</label>
                                      </div>
                                    </div>
                                    <div className="form-group">
                                      <label htmlFor="description">Description</label>
                                      <textarea id="description" disabled={this.state.submitted} onChange={(e) => this.updateInputState(e, key)} className="form-control" name="description" placeholder="Description" value={item.description} />
                                    </div>
                                  </div>

                                  <div className="Rule-Footer card-footer">
                                    {(Object.keys(item.matchers).length > 0)
                                      ? (
                                        <ul>
                                          {(Object.keys(item.matchers).map((matcher) => (
                                            <li key={`matcher-${matcher}`} className="list-group-item">
                                              <label htmlFor={`${matcher}`}>{matcher}</label>
                                              <div className="input-group">
                                                <input type="text" placeholder="Enter Text" onChange={(e) => this.updateMatcherState(e, key)} disabled={this.state.submitted} className="form-control" name={`${matcher}`} value={item.matchers[matcher]} />
                                                <div className="input-group-append">
                                                  <button onClick={(e) => this.onClickRemoveMatcher(e, key, matcher)} aria-label="remove" disabled={this.state.submitted} className="btn btn-rounded btn-primary" type="button"><i className="fa fa-close" /></button>
                                                </div>
                                              </div>
                                            </li>
                                          )))}
                                        </ul>
                                      )
                                      : null}
                                    <div className="Rule-Footer-Controls">
                                      <div className="input-group">
                                        <select onChange={(e) => this.updateMatcherSelectionState(e, key)} value={item.matcherSelection} disabled={this.state.submitted || this.state.loading} className="custom-select" aria-label="Example select with button addon">
                                          <option value="">Choose...</option>
                                          {this.state.optionsMatchers.map((i, matcherKey) => (
                                            // eslint-disable-next-line react/no-array-index-key
                                            <option key={matcherKey} value={i}>{i}</option>
                                          ))}
                                        </select>
                                        <div className="input-group-append">
                                          <button type="button" onClick={(e) => this.onClickAddMatcher(e, key)} disabled={this.state.submitted || this.state.loading} className="btn btn-rounded btn-info">Add Matcher</button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )
                              : null}
                          </div>

                          <div className="Rule-Drop" />
                        </div>
                      )))}
                  </div>
                </div>
              )}

            <div className="Rules-Controls">
              <button type="button" onClick={this.onClickAddRule} disabled={this.state.loading || this.state.submitted} className="btn btn-rounded btn-outline btn-primary">Add Rule</button>
              <span>&nbsp;</span>
              {(this.state.data && this.state.data.length > 0)
                ? (<button type="button" onClick={this.onClickPublishRules} disabled={this.state.loading || this.state.submitted} className="btn btn-rounded btn-primary">Publish Rules</button>)
                : null}
            </div>
          </div>

          <div className="col col-6 col-right">
            <Preview
              timestamp={this.state.preview}
              priority={this.state.priority}
              data={this.state.data}
            />
          </div>
        </div>

        {(this.state.modals && this.state.modals.bundle)
          ? (
            <Modal
              size="modal-lg"
              title="Bundle"
              loading={this.state.loadingModal}
              updateLoadingModal={this.updateLoadingModal}
              submitted={this.state.modalSubmitted}
              cancel={(event) => {
                this.updateShowHideModal('bundle', false);
                event.preventDefault();
              }}
              buttonCloseLabel="Close"
              buttonCloseAction={(event) => {
                this.updateShowHideModal('bundle', false);
                event.preventDefault();
              }}
              buttonSubmitAction={(event) => {
                const form = document.getElementById('modal-list-bundle');
                form.getElementsByTagName('button')[0].click();
                event.preventDefault();
              }}
            >
              <ModalBundle
                id="modal-list-bundle"
                loading={this.state.loadingModal}
                data={this.state.modalData}
                updateLoadingModal={this.updateLoadingModal}
                submitted={this.state.modalSubmitted}
                handleSubmit={this.updateModalSubmitted}
                handleSubmitSuccess={(data) => {
                  const { modals } = this.state;
                  modals.bundle = false;
                  this.setState((prevState) => ({
                    data: prevState.data.map((item) => (
                      (item.priority === data.priority) ? data : item)),
                    modals,
                    modalSubmitted: false,
                  }));
                }}
              />
            </Modal>
          )
          : null}

      </div>
    );
  }
}
/* ===========================
PROPERTY TYPES
=========================== */
RulesList.propTypes = {
  updateLoadingApp: PropTypes.func,
  updateLoadingContent: PropTypes.func,
};

/* ===========================
EXPORT
=========================== */
export default withRouter(RulesList);
